import { format } from "date-fns/format";
import { type GetAnnouncementsQuery } from "~/graphql/supergraphTypes";
import { toStandardFormat } from "~/utils/datetime";
import { AnnouncementListItem, AnnouncementStatus } from "./types";

/**
 * Formats a date object to a display string in the format "MMM d, yyyy".
 *
 * @param {Date} [date] - The date to format. If no date is provided, an empty string is returned.
 * @returns {string} The formatted date string in the format "MMM d, yyyy", or an empty string if no date is provided.
 */
export const formatDisplayDate = (date?: Date) => (date ? format(date, "MMM d, yyyy") : "");

/**
 * Formats a full name from first, middle, and last name components.
 *
 * @param {string | null} firstName - The first name.
 * @param {string | null} middleName - The middle name.
 * @param {string | null} lastName - The last name.
 * @returns {string} The formatted full name.
 */
export const formatFullName = (
  firstName: string | null,
  middleName: string | null,
  lastName: string | null,
): string => {
  return [firstName?.trim(), middleName?.trim(), lastName?.trim()].filter(Boolean).join(" ");
};

/**
 * Determines the status of an announcement based on its expiration and deletion dates.
 *
 * @param {Date | string} expiredAt - The expiration date of the announcement.
 * @param {Date | string | null} deletedAt - The deletion date of the announcement, or null if not deleted.
 * @returns {AnnouncementStatus} The status of the announcement.
 */
export const determineStatus = ({
  expiredAt,
  deletedAt,
}: {
  expiredAt: Date | string;
  deletedAt: Date | string | null;
}): AnnouncementStatus => {
  if (deletedAt) {
    return "Deleted";
  }
  const now = new Date();
  const expirationDate = new Date(expiredAt);
  if (expirationDate < new Date(now.toDateString())) {
    return "Inactive";
  }
  return "Active";
};

export const mapAnnouncements = (
  data?: GetAnnouncementsQuery,
): AnnouncementListItem[] | undefined => {
  if (!data || !data?.announcements) {
    return undefined;
  }

  const mappedAnnouncements = data.announcements.map((notification) => ({
    id: notification.id,
    name: notification.name,
    roles: notification.roles,
    roleCount: notification.roles?.length ?? 0,
    arenaGroups: notification.arenaGroups,
    header: notification.header,
    body: notification.body,
    createdBy: formatFullName(
      notification.createdByUser.firstName,
      notification.createdByUser.middleName,
      notification.createdByUser.lastName,
    ),
    url: notification.url,
    createdAt: notification.createdAt,
    expiredAt: new Date(toStandardFormat(notification.expiredAt)),
    deletedAt: notification.deletedAt,
    status: determineStatus({
      expiredAt: toStandardFormat(notification.expiredAt),
      deletedAt: notification.deletedAt,
    }),
    delete: {
      notificationId: notification.id,
      status: determineStatus({
        expiredAt: toStandardFormat(notification.expiredAt),
        deletedAt: notification.deletedAt,
      }),
    },
  }));

  mappedAnnouncements.sort((a, b) => {
    const aDate = new Date(a.createdAt);
    const bDate = new Date(b.createdAt);
    if (aDate !== bDate) {
      return bDate.getTime() - aDate.getTime();
    } else {
      return b.id - a.id;
    }
  });

  return mappedAnnouncements;
};
